import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Button } from "@mui/material";
import { Stack } from "@mui/system";
import { ClientFilterSelect } from "app/components/common/ClientFilterSelect";
import { UserFilterSelect } from "app/components/common/UserFilterSelect";
import { PeriodFilter, PERIOD_MODE_FULL, PERIOD_MODE_MONTH_YEAR } from "app/components/common/PeriodFilter";
import { ALL_USERS } from "AppConstants";

/**
 * The filter row shared by every effort screen: Client, Member / Manager, period, then the
 * screen's own action button. One source, so the four screens can no longer drift apart in
 * wording, ordering or spacing.
 *
 * Four props carry the scope rules HRMS-260 names, since a screen's scope decides which choices
 * may even be offered:
 *
 * - `showAllMemberManagerOption` (default true): the "All" entry under Member / Manager. False for
 *   an individual export, where one workbook covers one person.
 * - `showAllClientOption` (default true): the "All" entry under Client. False for a consolidated
 *   export, whose layout has no Account column and so covers exactly one client.
 * - `showMemberManagerDropdown` (default true): the Member / Manager control itself. False for a
 *   consolidated export, where the scope is a whole team and no person is picked.
 * - `showOnlyMonthPeriod` (default false): offers Year and Month alone, hiding the period type
 *   selector, for a report defined per calendar month.
 *
 * Every control stays presentational: the caller owns the values, the option lists and the fetching.
 *
 * `idPrefix` must be unique per bar: several bars share a page (the admin dashboard has
 * three), and duplicate DOM ids would point every label at the first field.
 * @since NEXT_VERSION
 */
// Row geometry, shared by the filter row and the action row beneath it so the two cannot drift
// apart in spacing or in how they collapse on a phone.
// @since NEXT_VERSION
const ROW_PROPS = {
  direction: { xs: "column", sm: "row" },
  spacing: 2,
  alignItems: { xs: "stretch", sm: "flex-start" },
  flexWrap: "wrap",
  useFlexGap: true,
};

export const EffortFilterBar = ({
  idPrefix,
  showClient = false,
  clientValue,
  onClientChange,
  clientOptions = [],
  showAllClientOption = true,
  showMemberManagerDropdown = true,
  userValue,
  onUserChange,
  // A screen whose report covers a whole scope rather than one person disables the person filter
  // and explains itself in its helper text. @since NEXT_VERSION
  userDisabled = false,
  userHelperText,
  memberOptions = [],
  managerOptions = [],
  selfOption = null,
  showAllMemberManagerOption = true,
  groupUsers = true,
  userLabel,
  showPeriod = true,
  showOnlyMonthPeriod = false,
  period,
  onPeriodChange,
  actionLabel = "Apply",
  onAction,
  // Greys the action out while the screen cannot act yet -- Export Reports uses it so a member
  // whose records have not resolved sees that before clicking rather than after.
  // @since NEXT_VERSION
  actionDisabled = false,
  // A screen that lays its action out on a row of its own turns the built-in button off and
  // renders one itself. Defaults on, so every existing caller keeps its single-row bar.
  // @since NEXT_VERSION
  showAction = true,
  // Moves the screen's own children and the action button to a row of their own, leaving the
  // filter row holding filters only -- so a screen carrying an extra control (Export Reports has
  // a Format picker) still reads like the plainest screen that uses this bar. Off by default, so
  // every existing caller keeps its single row.
  // @since NEXT_VERSION
  actionsBelow = false,
  // Screen-specific filters drawn ahead of the shared ones, for a screen whose first choice is not
  // a client or a person (Org-level Reports leads with Department). Empty by default.
  // @since NEXT_VERSION
  leadingFilters = null,
  children,
}) => {
  const actionButton = showAction && (
    <Button
      onClick={onAction}
      disabled={actionDisabled}
      sx={{ flexShrink: 0, width: { xs: "100%", sm: 150 }, m: 0 }}
      variant="contained"
      color="success"
    >
      {actionLabel}
    </Button>
  );

  // HRMS-260: with the Member / Manager dropdown hidden, the value is the list's first element --
  // "All" where that entry is offered, otherwise the first person. Reported back to the caller so
  // the request it builds matches what the screen means, rather than whatever happened to be
  // selected before the dropdown disappeared.
  // @since NEXT_VERSION
  const hiddenUserValue = showAllMemberManagerOption
    ? ALL_USERS
    : selfOption?.value ?? memberOptions[0]?.value ?? managerOptions[0]?.value ?? null;
  useEffect(() => {
    if (showMemberManagerDropdown || !onUserChange) return;
    if (userValue !== hiddenUserValue) {
      onUserChange(hiddenUserValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMemberManagerDropdown, hiddenUserValue]);

  const filterRow = (
    <Stack {...ROW_PROPS}>
    {leadingFilters}
    {showClient && (
      <ClientFilterSelect
        value={clientValue}
        onChange={onClientChange}
        options={clientOptions}
        includeAll={showAllClientOption}
        id={`${idPrefix}-client`}
      />
    )}
    {showMemberManagerDropdown && (
      <UserFilterSelect
        value={userValue}
        onChange={onUserChange}
        memberOptions={memberOptions}
        managerOptions={managerOptions}
        selfOption={selfOption}
        includeAll={showAllMemberManagerOption}
        grouped={groupUsers}
        disabled={userDisabled}
        helperText={userHelperText}
        {...(userLabel ? { label: userLabel } : {})}
        id={`${idPrefix}-user`}
      />
    )}
    {showPeriod && (
      <PeriodFilter
        value={period}
        onChange={onPeriodChange}
        mode={showOnlyMonthPeriod ? PERIOD_MODE_MONTH_YEAR : PERIOD_MODE_FULL}
        idPrefix={idPrefix}
      />
    )}
      {!actionsBelow && children}
      {!actionsBelow && actionButton}
    </Stack>
  );

  if (!actionsBelow) return filterRow;

  return (
    <Stack direction="column" spacing={2} useFlexGap>
      {filterRow}
      {/* Carries a test id because which row a control sits on is the substance of this mode, and
          the alternative is asserting against MUI's own class names. */}
      <Stack {...ROW_PROPS} data-testid={`${idPrefix}-action-row`}>
        {children}
        {actionButton}
      </Stack>
    </Stack>
  );
};

EffortFilterBar.propTypes = {
  idPrefix: PropTypes.string.isRequired,
  showClient: PropTypes.bool,
  clientValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClientChange: PropTypes.func,
  clientOptions: PropTypes.array,
  showAllClientOption: PropTypes.bool,
  showMemberManagerDropdown: PropTypes.bool,
  userValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onUserChange: PropTypes.func,
  userDisabled: PropTypes.bool,
  userHelperText: PropTypes.string,
  memberOptions: PropTypes.array,
  managerOptions: PropTypes.array,
  selfOption: PropTypes.object,
  showAllMemberManagerOption: PropTypes.bool,
  groupUsers: PropTypes.bool,
  userLabel: PropTypes.string,
  showPeriod: PropTypes.bool,
  showOnlyMonthPeriod: PropTypes.bool,
  period: PropTypes.object,
  onPeriodChange: PropTypes.func,
  actionLabel: PropTypes.string,
  onAction: PropTypes.func,
  actionDisabled: PropTypes.bool,
  actionsBelow: PropTypes.bool,
  showAction: PropTypes.bool,
  leadingFilters: PropTypes.node,
  children: PropTypes.node,
};

export default EffortFilterBar;
